import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { ArrowLeft, Download, Maximize2, Minimize2, Bookmark, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import HighlightDialog from "@/components/HighlightDialog";

/**
 * In-browser PDF reader for purchased books.
 * Relies on the browser's built-in PDF viewer (all modern browsers ship one).
 * The PDF is served from `${API}/files/{pdf_path}` with cookie auth (same origin
 * on Kubernetes ingress), so no additional auth token needs to be passed here.
 */
export default function Read() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [ownedChecked, setOwnedChecked] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [savedPage, setSavedPage] = useState(null);
  const [pageInput, setPageInput] = useState("");
  const [savingPage, setSavingPage] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/books/${id}`).then((r) => mounted && setBook(r.data)).catch(() => {});
    api.get("/library").then((r) => {
      if (!mounted) return;
      const owned = (r.data || []).some((b) => b.id === id);
      if (!owned) {
        toast.error("You don't own this book yet.");
        navigate(`/book/${id}`);
      } else {
        setOwnedChecked(true);
      }
    }).catch(() => {
      navigate(`/book/${id}`);
    });
    // Load saved reading position (if any)
    api.get(`/reading-progress/${id}`).then((r) => {
      if (mounted && r.data?.page) {
        setSavedPage(r.data.page);
        setPageInput(String(r.data.page));
      }
    }).catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!book || !ownedChecked) {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted_ink">Loading your book…</div>;
  }

  // Guard: some external / classics-linked books have no uploaded PDF; there's
  // nothing to render in-browser.
  if (!book.pdf_path) {
    return (
      <div data-testid="reader-no-pdf" className="max-w-2xl mx-auto px-6 py-24 text-center">
        <Bookmark className="w-10 h-10 text-terracotta mx-auto mb-6" strokeWidth={1.25} />
        <h1 className="font-serif text-4xl">No in-browser PDF for this book.</h1>
        <p className="text-muted_ink mt-4 leading-relaxed">
          This title links out to its original source. Open the book page for the external link.
        </p>
        <Link to={`/book/${id}`} className="btn-primary mt-8 inline-flex">Back to the book</Link>
      </div>
    );
  }

  const savePlace = async () => {
    const page = parseInt(pageInput, 10);
    if (!page || page < 1) {
      toast.error("Enter the page number you're on");
      return;
    }
    setSavingPage(true);
    try {
      const total = Number(book.pages) || 0;
      const { data } = await api.post(`/reading-progress/${id}`, { page, total_pages: total });
      setSavedPage(data.page);
      toast.success(`Bookmarked page ${data.page}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your place");
    } finally {
      setSavingPage(false);
    }
  };

  // Load PDF at the saved page (browser PDF viewer supports #page=N)
  const initialPage = savedPage || 1;
  const pdfUrl = `${API}/files/${book.pdf_path}#page=${initialPage}&toolbar=1&view=FitH`;

  return (
    <div data-testid="reader-page" className={`${fullscreen ? "fixed inset-0 z-50 bg-paper" : "max-w-6xl mx-auto px-4 md:px-8 py-6"}`}>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 gap-4">
        <Link to={`/book/${id}`} className="flex items-center gap-2 text-sm text-muted_ink hover:text-ink" data-testid="reader-back">
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </Link>
        <div className="flex-1 min-w-0 text-center">
          <div className="font-serif text-lg md:text-xl truncate">{book.title}</div>
          <div className="text-xs italic text-muted_ink truncate">by {book.author}</div>
        </div>
        <div className="flex items-center gap-2">
          <div data-testid="save-place" className="flex items-center gap-1 border border-edge bg-white pl-2">
            <MapPin className="w-3.5 h-3.5 text-muted_ink" strokeWidth={1.5} />
            <input
              data-testid="page-input"
              type="number"
              min={1}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              placeholder="Page"
              className="w-16 py-2 px-1 text-sm bg-transparent focus:outline-none"
            />
            <button
              data-testid="save-place-btn"
              onClick={savePlace}
              disabled={savingPage}
              className="text-xs uppercase tracking-widest px-3 py-2 border-l border-edge hover:bg-linen disabled:opacity-60 inline-flex items-center gap-1"
              title="Save my place"
            >
              {savingPage ? "…" : <><Check className="w-3.5 h-3.5" /> <span className="hidden md:inline">Save</span></>}
            </button>
          </div>
          <button
            data-testid="reader-highlight"
            onClick={() => setHighlightOpen(true)}
            className="text-xs uppercase tracking-widest border border-terracotta bg-terracotta text-paper px-3 py-2 hover:bg-ink hover:border-ink inline-flex items-center gap-1"
          >
            <Bookmark className="w-3.5 h-3.5" /> <span className="hidden md:inline">Save a highlight</span>
          </button>
          <a
            href={`${API}/files/${book.pdf_path}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs uppercase tracking-widest border border-edge px-3 py-2 hover:bg-linen inline-flex items-center gap-1"
            data-testid="reader-download"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </a>
          <button
            onClick={() => setFullscreen((v) => !v)}
            className="text-xs uppercase tracking-widest border border-edge px-3 py-2 hover:bg-linen inline-flex items-center gap-1"
            data-testid="reader-fullscreen"
            title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            <span className="hidden md:inline">{fullscreen ? "Exit" : "Focus"}</span>
          </button>
        </div>
      </div>

      {!fullscreen && savedPage && (
        <div data-testid="continue-banner" className="mt-3 mb-1 flex items-center justify-between border border-terracotta/30 bg-linen px-4 py-2 text-sm">
          <div className="flex items-center gap-2">
            <Bookmark className="w-3.5 h-3.5 text-terracotta" strokeWidth={1.5} />
            <span>Picking up on <strong>page {savedPage}</strong> — where you left off.</span>
          </div>
        </div>
      )}

      {/* PDF Viewer */}
      <div
        data-testid="pdf-viewer"
        className="border border-edge bg-white"
        style={{ height: fullscreen ? "calc(100vh - 72px)" : "80vh" }}
      >
        <iframe
          title={book.title}
          src={pdfUrl}
          className="w-full h-full"
          style={{ border: 0 }}
        />
      </div>

      {!fullscreen && (
        <p className="text-xs text-muted_ink text-center mt-4">
          Your PDF is streaming from Folio — no download required. Tap <span className="uppercase tracking-widest">Focus</span> for a fullscreen read, or <span className="uppercase tracking-widest">Save a highlight</span> to keep a passage in your commonplace book.
        </p>
      )}

      <HighlightDialog
        open={highlightOpen}
        onOpenChange={setHighlightOpen}
        bookId={id}
        bookTitle={book.title}
      />
    </div>
  );
}
